import React, { memo } from 'react';

import { Box, FormHelperText, IconButton } from '@mui/material';
import { DeleteIcon } from '@sharedCR/coreDesignSystem/icons';
import { CheckboxCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DenialReasonFieldTestIds } from './DenialReasonField.testIds';

const getStyles = ({ borrowersCount } = {}) => ({
  denialData: {
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'center',
    flexGrow: 1,
  },
  borrowers:
    borrowersCount > 1
      ? {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, auto))',
          gridColumnGap: '15px',
        }
      : {},
});

const DenialReasonField = ({
  denialReason,
  allDenialReasonsData,
  index,
  isLoading,
  handleChange,
  onDeleteDenialReason,
  borrowerOptions,
  validationErrors,
}) => {
  const { borrowers, name, value } = denialReason;
  const { isDenialDueToCredit, isAffiliate } = allDenialReasonsData[value];
  const borrowersCount = Object.values(borrowers ?? {}).length;
  const classes = getStyles({ borrowersCount });
  const borrowersError = validationErrors?.[index]?.borrowers;
  const errorText = _.isString(borrowersError) ? borrowersError : null;

  return (
    <>
      <div className="flex gap-20 items-center justify-between">
        <TextFieldCDS
          disabled
          noValidate
          className="min-w-224 max-w-xs"
          dataTestId={DenialReasonFieldTestIds.getReasonInputId(index)}
          label="Denial Reason"
          margin="normal"
          name={`denialReasons[${index}].name`}
          value={name}
          variant="outlined"
        />
        <Box sx={classes.denialData}>
          {isAffiliate && (
            <CheckboxCDS
              disabled
              dataTestId={DenialReasonFieldTestIds.getAffiliateCheckboxId(index)}
              label="Affiliate Credit"
              value={isAffiliate}
            />
          )}
          {isDenialDueToCredit && (
            <CheckboxCDS
              disabled
              dataTestId={DenialReasonFieldTestIds.getCreditDenialCheckboxId(index)}
              label="Credit Denial"
              value={isDenialDueToCredit}
            />
          )}
          {borrowersCount > 0 && (
            <Box sx={classes.borrowers}>
              {borrowerOptions.map(({ value: id, name }, i) => (
                <CheckboxCDS
                  data-denialreasonindex={index}
                  data-id={id}
                  dataTestId={DenialReasonFieldTestIds.getBorrowerCheckboxId(index, i)}
                  disabled={isLoading || borrowersCount === 1}
                  error={errorText}
                  key={id}
                  label={name}
                  name={`denialReasons[${index}].borrowers[${i}].value`}
                  value={borrowers[i].value}
                  onChange={handleChange}
                />
              ))}
            </Box>
          )}
        </Box>
        <IconButton
          data-id={index}
          data-testid={DenialReasonFieldTestIds.getDeleteButtonId(index)}
          disabled={isLoading}
          onClick={onDeleteDenialReason}
        >
          <DeleteIcon />
        </IconButton>
      </div>
      {errorText && (
        <FormHelperText error className="ml-256 -mt-3">
          {errorText}
        </FormHelperText>
      )}
    </>
  );
};

DenialReasonField.propTypes = {
  ...WithLoadingPropTypes,
  borrowerOptions: PropTypes.array.isRequired,
  denialReason: PropTypes.object.isRequired,
  allDenialReasonsData: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onDeleteDenialReason: PropTypes.func.isRequired,
  validationErrors: PropTypes.array,
};

export default memo(DenialReasonField);
